import React, { ChangeEvent } from 'react';
import {
  SelectCard,
  TextArea,
  IconTools,
  IconCancel,
  IconPaymentCircle,
} from 'gatorcomponents';
import useLocale from '@/hooks/useLocale';
import type { CancelSurveyLocale, CancelSurveyProps } from './CancelSurvey.types';
import * as Styles from './CancelSurvey.styles';

const CATEGORY_ICONS: Record<number, React.ElementType> = {
  1: IconTools,
  2: IconCancel,
  3: IconPaymentCircle,
};

const COMMENT_MAX_LENGTH = 800;

const CancelSurvey = ({
  question,
  selectedCategoryId,
  selectedOptionId,
  comment = '',
  onCategorySelect,
  onOptionSelect,
  onCommentChange,
  testId = 'cancel-survey',
}: CancelSurveyProps) => {
  const { billing } = useLocale() as { billing?: { subscriptions?: { cancel?: { survey?: CancelSurveyLocale } } } };
  const surveyLocale: CancelSurveyLocale = billing?.subscriptions?.cancel?.survey ?? {};

  if (!question) {
    return null;
  }

  const handleCategoryClick = (id: number) => () => onCategorySelect?.(id);

  const handleSelectOption = ({ value }: { value: string }) => {
    if (onOptionSelect) {
      onOptionSelect(Number(value));
    }
  };

  const handleCommentChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    if (onCommentChange) {
      onCommentChange(event.currentTarget.value);
    }
  };

  return (
    <Styles.Wrapper data-testid={testId}>
      <Styles.Title data-testid={`${testId}-title`}>
        {question.value}
      </Styles.Title>

      <Styles.Description data-testid={`${testId}-description`}>
        <p>{surveyLocale.surveyQuestionDescriptionLabel}</p>
        <p>{surveyLocale.surveyReasonQuestionLabel}</p>
      </Styles.Description>

      <Styles.CategoryList data-testid={`${testId}-category-list`}>
        {question.reasons.map((reason) => {
          const Icon = CATEGORY_ICONS[reason.id];
          const isSelected = selectedCategoryId === reason.id;
          const options = reason.options.map(option => ({
            value: String(option.id),
            label: option.value,
          }));
          const cardValue = selectedOptionId != null
            ? String(selectedOptionId)
            : undefined;

          return (
            <SelectCard
              key={reason.id}
              testId={`${testId}-category-${reason.id}`}
              icon={Icon ? <Icon /> : null}
              label={reason.label}
              selected={isSelected}
              onClick={handleCategoryClick(reason.id)}
              options={options}
              value={cardValue}
              onSelectOption={handleSelectOption}
            />
          );
        })}

        {selectedCategoryId != null && (
          <Styles.CommentWrapper data-testid={`${testId}-comment-wrapper`}>
            <TextArea
              testId={`${testId}-comment`}
              name="cancel-survey-comment"
              label=""
              value={comment}
              onChange={event => handleCommentChange(event as ChangeEvent<HTMLTextAreaElement>)}
              maxLength={COMMENT_MAX_LENGTH}
              counterFormat="typed"
              remainingCharactersPosition="outside"
            />
          </Styles.CommentWrapper>
        )}
      </Styles.CategoryList>
    </Styles.Wrapper>
  );
};

export default CancelSurvey;
